/// <reference lib="dom" />
import * as d3 from "d3";
import type { ChartLayer, ChartScale } from "../core/chart.ts";
import { type ChartTickFormat, ScaleIn, Single } from "./shared.ts";

export interface AxisLayerOpts {
  side: "top" | "bottom" | "left" | "right";
  ticks?: number;
  values?: number[];
  format?: ChartTickFormat;
  tickSize?: number;
  scale?: ChartScale;
  inside?: boolean;
  enabled?: () => boolean;
}

export function AxisLayer(opts: AxisLayerOpts): ChartLayer {
  const inside = opts.inside ?? true;
  const facing = inside
    ? ({ top: "bottom", bottom: "top", left: "right", right: "left" } as const)[opts.side]
    : opts.side;
  const axisOf = {
    top: d3.axisTop,
    bottom: d3.axisBottom,
    left: d3.axisLeft,
    right: d3.axisRight,
  }[facing];
  const horizontal = opts.side === "top" || opts.side === "bottom";
  return {
    id: `axis-${opts.side}`,
    render(root, ctx) {
      if (opts.enabled && !opts.enabled()) {
        root.selectAll("*").remove();
        return;
      }
      const scale = ScaleIn(ctx, horizontal ? "x" : "y", opts.scale);
      const { inner } = ctx.frame;
      const axis = axisOf(scale)
        .ticks(opts.ticks ?? 8)
        .tickSizeOuter(0)
        .tickSizeInner(opts.tickSize ?? 0)
        .tickPadding(2);
      if (opts.values) axis.tickValues(opts.values);
      if (opts.format) axis.tickFormat(opts.format(scale) as (v: d3.NumberValue) => string);
      const offset = {
        top: [0, inner.top],
        bottom: [0, inner.bottom],
        left: [inner.left, 0],
        right: [inner.right, 0],
      }[opts.side];
      const g = Single<SVGGElement>(root, "g", "axis")
        .attr("transform", `translate(${offset[0]},${offset[1]})`)
        .call(
          axis as unknown as (g: d3.Selection<d3.BaseType, number, SVGGElement, unknown>) => void,
        )
        .attr("font-size", ctx.theme.fontSize)
        .attr("font-family", null)
        .style("pointer-events", "none");
      g.select(".domain").remove();
      g.selectAll(".tick line").attr("stroke", ctx.theme.axis);
      g.selectAll(".tick text").attr("fill", ctx.theme.text);
      const [r0, r1] = scale.range();
      const edge = inside ? (horizontal ? ctx.theme.fontSize : ctx.theme.fontSize / 2 + 1) : -0.5;
      const lo = Math.min(r0, r1) + edge;
      const hi = Math.max(r0, r1) - edge;
      g.selectAll<SVGGElement, number>(".tick")
        .attr("display", (v) => (scale(v) < lo || scale(v) > hi ? "none" : null));
    },
  };
}
